import AppService from './AppService';
import apiClient from './apiClient';

/**
 * Service for handling ticket and ticket payment related API calls
 */
class TicketService {
  /**
   * Get list of unpaid tickets for a customer
   * @param {string} customerId Customer/Organization ID
   * @returns {Promise} Promise object with unpaid tickets list
   */
  static async getUnpaidTickets(customerId, params) {
    try {
      // AppService.get(url, X) already wraps X into axios's { params: X }, so
      // we pass the raw params object — not { params: {...} } — to avoid
      // ?params[page]=0 style double-wrapping.
      const response = await AppService.get(
        `/api/v1/tickets/${customerId}/unpaid`,
        params || undefined
      );
      return response;
    } catch (error) {
      console.error('Error fetching unpaid tickets from API:', error);
      return params?.page !== undefined ? { items: [], totalElements: 0, totalUnpaidAmount: 0 } : [];
    }
  }

  /**
   * Get count of unpaid tickets for a customer
   * @param {string} customerId Customer/Organization ID
   * @returns {Promise} Promise object with count
   */
  static countUnpaidTickets(customerId) {
    return AppService.get(`/api/v1/tickets/${customerId}/unpaid/count`);
  }

  /**
   * Get tickets for a customer (all payment statuses) — Ticket History tab.
   * When `params.page` is provided, the API returns a paginated map
   * `{items, totalElements, totalPages, page, pageSize}` and filtering by
   * `paymentStatus` is pushed to the server. Without `params.page` the
   * legacy `List<UnpaidTicketDTO>` shape is returned.
   *
   * @param {string} customerId Customer/Organization ID
   * @param {Object} [params] Optional { page, pageSize, paymentStatus }
   */
  static async getAllTickets(customerId, params) {
    try {
      // AppService.get(url, X) already wraps X into axios's { params: X }.
      // Passing the raw params object here keeps the query string as
      // ?page=0&pageSize=20&paymentStatus=ALL instead of params[page]=0...
      const response = await AppService.get(
        `/api/v1/tickets/${customerId}/all`,
        params || undefined
      );
      return response;
    } catch (error) {
      console.error('Error fetching all tickets from API:', error);
      return params?.page !== undefined ? { items: [], totalElements: 0 } : [];
    }
  }

  /**
   * Get payment preview before processing
   * @param {string} customerId Customer/Organization ID
   * @param {Object} request Payment request with ticketIds and paymentAmount
   * @returns {Promise} Promise object with payment preview details
   */
  static async getPaymentPreview(customerId, request) {
    try {
      return await AppService.post(`/api/v1/tickets/${customerId}/payment-preview`, request);
    } catch (error) {
      console.error('Error getting payment preview:', error);
      // Return mock preview data if API fails
      return {
        totalPaymentAmount: request.paymentAmount || 0,
        currentWalletBalance: 0,
        walletBalanceAfterPayment: -(request.paymentAmount || 0)
      };
    }
  }

  /**
   * Validate payment request before processing
   * @param {string} customerId Customer/Organization ID
   * @param {Object} request Payment request with ticketIds and paymentAmount
   * @returns {Promise} Promise object with validation result
   */
  static async validatePayment(customerId, request) {
    try {
      return await AppService.post(`/api/v1/tickets/${customerId}/validate-payment`, request);
    } catch (error) {
      console.error('Error validating payment:', error);
      return { isValid: true, validationMessage: 'OK' };
    }
  }

  /**
   * Process wallet payment for tickets
   * @param {string} customerId Customer/Organization ID
   * @param {Object} request Payment request with ticketIds and paymentAmount
   * @returns {Promise} Promise object with payment response
   */
  static async processWalletPayment(customerId, request) {
    try {
      return await AppService.post(`/api/v1/tickets/${customerId}/pay-with-wallet`, request);
    } catch (error) {
      console.error('Error processing wallet payment:', error);
      throw error; // Re-throw for component to handle
    }
  }

  /**
   * Process offline payment for tickets
   * @param {string} customerId Customer/Organization ID
   * @param {Object} request Payment request with ticketIds, paymentAmount, and paymentMethod
   * @returns {Promise} Promise object with payment response
   */
  static async processOfflinePayment(customerId, request) {
    try {
      // Try calling the /pay-offline endpoint first
      return await AppService.post(`/api/v1/tickets/${customerId}/pay-offline`, request);
    } catch (error) {
      console.error('Error with offline payment endpoint, trying validate-payment:', error);
      // Fallback to validate-payment endpoint if offline endpoint doesn't exist
      try {
        return await AppService.post(`/api/v1/tickets/${customerId}/validate-payment`, request);
      } catch (fallbackError) {
        console.error('Both offline and validate endpoints failed:', fallbackError);
        throw error;
      }
    }
  }

  /**
   * Get ticket details
   * @param {string} ticketId Ticket ID
   * @returns {Promise} Promise object with ticket details
   */
  static getTicket(ticketId) {
    return AppService.get(`/api/v1/tickets/${ticketId}`);
  }

  /**
   * Get all tickets for a customer
   * @param {string} customerId Customer/Organization ID
   * @param {Object} params Query parameters (page, size, sort, etc.)
   * @returns {Promise} Promise object with tickets list
   */
  static getTickets(customerId, params = {}) {
    return AppService.get(`/api/v1/tickets/${customerId}`, { params });
  }

  /**
   * Get customer's configured payment methods (banks, payment sources)
   * @param {string} customerId Customer/Organization ID
   * @returns {Promise} Promise object with payment methods list
   */
  static async getCustomerPaymentMethods(customerId) {
    try {
      const response = await AppService.get('v1/customerPaymentMethod/myPaymentMethods');
      console.log('API response for customer payment methods:', response);
      return response;
    } catch (error) {
      console.error('Error fetching customer payment methods from API:', error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Get all available payment methods (configured by Root Admin)
   * @returns {Promise} Promise object with all available payment methods
   */
  static async getAvailablePaymentMethods() {
    try {
      const response = await AppService.get('v1/paymentMethod/keyValues');
      console.log('API response for available payment methods:', response);
      return response;
    } catch (error) {
      console.error('Error fetching available payment methods from API:', error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Get all topup requests (payment history) for the current user
   * @returns {Promise} Promise object with topup requests list
   */
  static async getTopupRequests() {
    try {
      const response = await AppService.get('v1/topupRequest');
      console.log('API response for topup requests:', response);
      return response;
    } catch (error) {
      console.error('Error fetching topup requests from API:', error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Get payment summary statistics (total approved, pending, rejected amounts and counts)
   * @returns {Promise} Promise object with payment summary data
   */
  static async getPaymentSummary() {
    try {
      const response = await AppService.get('v1/topupRequest/summary');
      console.log('API response for payment summary:', response);
      return response;
    } catch (error) {
      console.error('Error fetching payment summary from API:', error);
      // Return empty summary on error
      return {
        totalApprovedAmount: 0,
        totalPendingAmount: 0,
        totalRejectedAmount: 0,
        approvedCount: 0,
        pendingCount: 0,
        rejectedCount: 0,
        currentWalletBalance: 0,
        availableBalance: 0
      };
    }
  }

  /**
   * Get pending topup requests only (awaiting admin approval)
   * @returns {Promise} Promise object with pending topup requests list
   */
  static async getPendingTopupRequests() {
    try {
      const response = await AppService.get('v1/topupRequest/pending');
      console.log('API response for pending topup requests:', response);
      return response;
    } catch (error) {
      console.error('Error fetching pending topup requests from API:', error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Submit a topup request for wallet top-up
   * @param {Object} topupRequest The topup request object with amount and paymentMethodId
   * @returns {Promise} Promise object with submission response
   */
  static async submitTopupRequest(topupRequest) {
    try {
      console.log('Submitting topup request:', topupRequest);
      const response = await AppService.post('v1/topupRequest', topupRequest);
      console.log('Topup request submitted successfully:', response);
      return response;
    } catch (error) {
      console.error('Error submitting topup request:', error);
      throw error;
    }
  }

  static async uploadTopupProof(topUpId, file) {
    try {
      if (!topUpId) throw new Error('topUpId is required');
      if (!file) throw new Error('file is required');

      const fd = new FormData();
      fd.append('file', file);

      const resp = await apiClient.post('/v1/topupRequest/upload-proof', fd, {
        params: { topUpId },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      return resp?.data;
    } catch (error) {
      console.error('Error uploading topup proof:', error);
      throw error;
    }
  }

  /**
   * Submit a ticket payment request (offline payment)
   * @param {string} customerId The customer/organization ID
   * @param {Object} paymentRequest The payment request object with ticketIds, amount, and paymentMethod
   * @returns {Promise} Promise object with submission response
   */
  static async submitTicketPayment(customerId, paymentRequest) {
    try {
      console.log('Submitting ticket payment:', paymentRequest);
      const response = await AppService.post(`/api/v1/tickets/${customerId}/offline-payment`, paymentRequest);
      console.log('Ticket payment submitted successfully:', response);
      return response;
    } catch (error) {
      console.error('Error submitting ticket payment:', error);
      throw error;
    }
  }
}

export default TicketService;
